import isMobilePhone from 'validator/es/lib/isMobilePhone';
import { MOBILE_LOCALE } from 'config/const';

/**
 * This regular expression looks for any character that is NOT:
 * - a letter in any script (`\p{L}` — covers `a-z`, `A-Z`, accented letters like `Á`/`ñ`/`ö`,
 *   ligatures like `ß`/`Æ`, and non-Latin scripts)
 * - a number (`\p{N}`)
 * - a whitespace character (space, tab, newline, etc.)
 *
 * It matches any character that falls outside of these categories.
 *
 * **Unicode-aware on purpose.** An ASCII-only class (`[^a-zA-Z0-9\s]`) treats every accented or
 * non-Latin letter as a symbol, which sorts sites like "Ángel Luna" ahead of every other name via
 * {@link sortAlphaNumerically}'s symbols-first rule. The Unicode Collation Algorithm gives accents a
 * secondary weight — they belong alongside their base letter — and `localeCompare` already handles
 * them correctly, so they must not be intercepted as symbols first.
 */
const SYMBOL_REGEX = /[^\p{L}\p{N}\s]/u;

/**
 * Checks if a string contains any symbols.
 *
 * @param value the string to check
 * @returns boolean
 */
export const isSymbol = (value: string) => SYMBOL_REGEX.test(value);

/**
 * like lodash.capitalize (https://docs-lodash.com/v4/capitalize/),
 * this function will capitalize the first letter of a string and
 * convert the remainder of the string to lowercase.
 *
 * @param str the string to capitalize
 * @returns the capitalized string
 */
export const capitalize = (str = '') => {
  const lower = str.toString().toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/**
 * Finds any number of sequential spaces and replaces them with only 1 space,
 * then trims the string
 *
 * @param str the string to strip of spaces
 * @returns the stripped string
 */
export const stripSpaces = (str = '') => str.replace(/\s+/g, ' ').trim();

/**
 * Checks a string to see if the first character is a vowel
 * This is useful for having to build sentences
 *
 * @param str the string to check
 * @returns boolean
 */
export const beginsWithVowel = (str: string): boolean => ['a', 'e', 'i', 'o', 'u'].indexOf(str.trim()[0].toLowerCase()) !== -1;

/**
 * Generates initials from a string, based on the specified number of words.
 *
 * The function splits the input string into words, trims any extra spaces,
 * and extracts the first letter of each word up to the specified count.
 * The initials are returned as a concatenated uppercase string.
 *
 * @param value - The input string to generate initials from. If undefined or empty, an empty string will be returned.
 * @param initialCount - The maximum number of initials to include in the result. If greater than the number of words, it will use the available words.
 * @returns A string containing the uppercase initials of the first letters of the words, up to the specified count.
 */

export const getInitials = (value: string, initialCount: number) => {
  // Split the string by spaces and filter out empty strings caused by multiple spaces
  const values = value?.trim().split(/\s+/) || [''];

  // Get the minimum between initialCount and number of words
  const amountOfInitials = Math.min(values.length, initialCount);

  // Slice the array to the required initial count and generate initials
  return values.slice(0, amountOfInitials).reduce((acc: string, word: string) => {
    if (word) return acc + word.charAt(0).toUpperCase();
    return acc;
  }, '');
};

/**
 * Converts a given string to title case. It capitalizes the first letter of each word
 * and handles special cases like parentheses and apostrophes.
 *
 * @param {string} message - The input string to be converted.
 * @returns {string} The string converted to title case.
 */
export const toTitleCase = (message: string): string => `${message?.toLowerCase().replace(/(^\w{1})|(\s+\w{1})|(\(\w{1})|('\w{1})/g, (letter) => letter.toUpperCase())}`;

/**
 * Normalizes a given string by converting it to lowercase and removing all non-alphanumeric characters.
 *
 * This function performs the following transformations on the input string:
 * 1. Converts all characters to lowercase using `toLowerCase()`.
 * 2. Removes any characters that are not letters or digits using a regular expression.
 *
 * The regular expression `/[^a-zA-Z0-9]/gi` matches any character that is not a letter or digit,
 * and the `replace` method removes these characters from the string.
 *
 * @param value - The string to be normalized.
 * @returns The normalized string, with all characters in lowercase and all non-alphanumeric characters removed.
 */
export const normalizeString = (value: string) => value.toLowerCase().replace(/[^a-zA-Z0-9]/gi, '');

/**
 * Formats a given mobile phone number string into the standard U.S. format (XXX-XXX-XXXX).
 *
 * @param {string} mobile - The mobile phone number as a string of digits.
 *                          The input string should contain exactly 10 digits.
 *
 * @returns {string} - The formatted mobile phone number as a string in the format XXX-XXX-XXXX.
 */
export const formatMobile = (mobile: string): string => `${mobile.slice(0, 3)}-${mobile.slice(3, 6)}-${mobile.slice(6, 10)}`;

/**
 * Removes the US country code (+1 or 1) from the beginning of a valid phone number string.
 *
 * @param value - The phone number string, possibly with a country code.
 * @returns The phone number string with the country code removed, or the original string if no US country code was found.
 */
export const stripCountryCode = (value?: string) => {
  if (isMobilePhone(value || '', MOBILE_LOCALE)) {
    return value.startsWith('1') && value.length > 10 ? value.substring(1).trim() : value.replace('+1', '').trim();
  }
  return value;
};

/**
 * Sanitizes a given string to be used as a safe file name.
 *
 * @param {string} input - The string to be sanitized.
 * @returns {string} - The sanitized string, suitable for use as a file name.
 */
export const sanitizeFileName = (input: string): string => {
  const replacementString = '_';

  return input
    .normalize('NFD') // Normalize accents and diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/\s*-\s*/g, '-') // Remove spaces around hyphens while keeping them
    .replace(/\s+/g, replacementString) // Replace spaces with underscores
    .replace(/[/?<>\\:*|"]/g, replacementString) // Replace invalid file name characters
    .replace(/[^a-zA-Z0-9\-_.]/g, '') // Remove unwanted characters except valid ones
    .replace(new RegExp(`${replacementString}{2,}`, 'g'), replacementString) // Collapse multiple replacements
    .replace(new RegExp(`^${replacementString}+|${replacementString}+$`, 'g'), '') // Trim leading/trailing underscores
    .replace(/^-+|-+$/g, '') // Trim leading/trailing hyphens
    .substring(0, 255); // Limit length to 255 characters
};
